import { useState, useEffect } from 'react'

export default function GalleryViewer({ slug }) {
  const [gallery, setGallery] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeIndex, setActiveIndex] = useState(null)

  useEffect(() => {
    let isMounted = true

    async function fetchGallery() {
      try {
        setLoading(true)
        // Try fetching by slug first
        const slugRes = await fetch(`https://api.dev.auxo.dk/public/by-slug/${slug}`)
        if (slugRes.ok) {
          const data = await slugRes.json()
          if (isMounted) {
            setGallery(data)
            setLoading(false)
            return
          }
        }

        // Fallback: fetch all galleries and find by slug
        const listRes = await fetch('https://api.dev.auxo.dk/public/content/gallery')
        if (listRes.ok) {
          const list = await listRes.json()
          const found = list.find((item) => item.slug === slug || (item.slug && item.slug._ === slug))
          if (found && isMounted) {
            setGallery(found)
            setLoading(false)
            return
          }
        }

        throw new Error('Galleriet kunne ikke findes')
      } catch (err) {
        console.error('Error fetching gallery:', err)
        if (isMounted) {
          setError('Kunne ikke indlæse galleribilleder.')
          setLoading(false)
        }
      }
    }

    fetchGallery()

    return () => {
      isMounted = false
    }
  }, [slug])

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (activeIndex === null) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setActiveIndex(null)
      } else if (e.key === 'ArrowRight') {
        handleNext()
      } else if (e.key === 'ArrowLeft') {
        handlePrev()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [activeIndex, gallery])

  // Prevent background scroll when lightbox is open
  useEffect(() => {
    if (activeIndex !== null) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [activeIndex])

  const handlePrev = () => {
    if (!gallery || !gallery.images) return
    setActiveIndex((prev) => (prev === 0 ? gallery.images.length - 1 : prev - 1))
  }

  const handleNext = () => {
    if (!gallery || !gallery.images) return
    setActiveIndex((prev) => (prev === gallery.images.length - 1 ? 0 : prev + 1))
  }

  // Helper to get Cloudflare resized image URL
  const getResizedUrl = (url, width, quality = 80) => {
    if (!url || !url.startsWith('https://assets.auxodev.dk/')) return url
    return url.replace(
      'https://assets.auxodev.dk/',
      `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=${quality},format=auto/`
    )
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-warm-gray-200 border-t-brand-green" />
        <p className="mt-4 text-sm text-warm-gray-500 font-medium">Henter galleri...</p>
      </div>
    )
  }

  if (error || !gallery || !gallery.images || gallery.images.length === 0) {
    return (
      <div className="rounded-lg bg-warm-gray-100 p-8 text-center border border-warm-gray-200">
        <i className="fa-light fa-images text-3xl text-warm-gray-400 mb-3 block" />
        <p className="text-sm text-warm-gray-600 font-medium">{error || 'Ingen billeder tilgængelige i dette galleri.'}</p>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {gallery.images.map((imgUrl, idx) => (
          <button
            key={idx}
            onClick={() => setActiveIndex(idx)}
            className="group relative aspect-4/3 overflow-hidden bg-warm-gray-100 border border-warm-gray-200 shadow-sm hover:shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2"
          >
            <img
              src={getResizedUrl(imgUrl, 600)}
              alt={`${gallery.title} referencebillede ${idx + 1}`}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            {/* Hover Overlay */}
            <div className="absolute inset-0 bg-anthracite/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <div className="h-12 w-12 rounded-full bg-white/90 flex items-center justify-center text-brand-green shadow-lg transform scale-90 group-hover:scale-100 transition-transform duration-300">
                <i className="fa-light fa-magnifying-glass-plus text-lg" />
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Fullscreen Lightbox */}
      {activeIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm animate-fade-in duration-200">
          {/* Close Button */}
          <button
            onClick={() => setActiveIndex(null)}
            className="absolute top-6 right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus:outline-none"
            aria-label="Luk galleri"
          >
            <i className="fa-light fa-xmark text-xl" />
          </button>

          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            className="absolute left-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus:outline-none"
            aria-label="Forrige billede"
          >
            <i className="fa-light fa-chevron-left text-xl" />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors focus:outline-none"
            aria-label="Næste billede"
          >
            <i className="fa-light fa-chevron-right text-xl" />
          </button>

          {/* Image Container */}
          <div className="relative max-h-[85vh] max-w-[90vw] flex flex-col items-center justify-center">
            <img
              src={getResizedUrl(gallery.images[activeIndex], 1600, 85)}
              alt={`${gallery.title} fuld skærm ${activeIndex + 1}`}
              className="max-h-[80vh] max-w-full object-contain shadow-2xl select-none"
            />
            {/* Image Counter */}
            <div className="mt-4 text-sm font-medium text-warm-gray-400">
              {activeIndex + 1} / {gallery.images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
